import React, { useEffect, useState } from 'react';
import { Search, Users, ClipboardList, CalendarClock, ShieldCheck, ArrowRight } from 'lucide-react';
import { api, DEMO } from '../lib/api';
import { hasCap } from '../lib/capabilities';
import '../components/SectionPhpEligibility.css';
import './Eligibility.css';

const STATUS = {
  ELIGIBLE: { label: 'Eligible', tone: 'pass' },
  NOT_ELIGIBLE: { label: 'Not eligible', tone: 'fail' },
  PENDING_INFORMATION: { label: 'Pending information', tone: 'pending' },
  PENDING_PHYSICIAN_CERTIFICATION: { label: 'Pending physician certification', tone: 'pending' },
  REQUIRES_HIGHER_LEVEL_OF_CARE: { label: 'Requires higher level of care', tone: 'safety' },
};

export default function Eligibility({ user, facility, navigate, notify, store }) {
  const [data, setData] = useState(null), [loading, setLoading] = useState(true), [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (DEMO) { setLoading(false); return; }
    let active = true;
    api('/php/eligibility-overview')
      .then(d => { if (active) setData(d); })
      .catch(e => { if (active) setError(e.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const patients = DEMO
    ? (store.patients || []).filter(p => String(p.facility_id) === String(user.facility_id)).map(p => ({ patient_id: p.id, name: `${p.first_name} ${p.last_name}`, mrn: p.mrn, admission_status: p.admission_status, eligibility_status: null, enrollment: null, recertification: { overdue: false, upcoming: false } }))
    : (data?.patients || []);

  const filtered = patients.filter(p => {
    const q = query.toLowerCase();
    if (filter === 'all') return !q || `${p.name} ${p.mrn || ''}`.toLowerCase().includes(q);
    if (filter === 'pending') return (p.eligibility_status === null || p.eligibility_status?.startsWith('PENDING') || p.eligibility_status === 'REQUIRES_HIGHER_LEVEL_OF_CARE') && (!q || `${p.name} ${p.mrn || ''}`.toLowerCase().includes(q));
    if (filter === 'overdue') return p.recertification?.overdue && (!q || `${p.name} ${p.mrn || ''}`.toLowerCase().includes(q));
    return true;
  });

  const stat = (label, n) => ({ label, n });
  const stats = [
    [ClipboardList, 'Total patients', patients.length],
    [ShieldCheck, 'Eligible', patients.filter(p => p.eligibility_status === 'ELIGIBLE').length],
    [CalendarClock, 'Recert due / overdue', patients.filter(p => p.recertification?.overdue || p.recertification?.upcoming).length],
  ];

  const openChart = p => navigate(`/patient/${p.patient_id}`);

  return <>
    <div className="page-heading"><div><div className="eyebrow">{facility?.name || 'YOUR FACILITY'} · PHP / IOP ELIGIBILITY</div><h1>Medicare PHP eligibility, facility-wide.</h1><p>Review the latest eligibility determination, program enrollment, and recertification status for every patient.</p></div></div>

    {DEMO && <div className="php-safety-alert" style={{ borderColor: '#b45309', background: '#fffbeb', color: '#92400e' }}><div><strong>Eligibility management requires live data.</strong><p>The eligibility engine runs server-side against the A57053 code list and certification records, so the facility-wide overview is available in the authenticated (non-demo) workspace.</p></div></div>}

    <div className="registry-stats">{stats.map(([Icon, label, n]) => <div key={label}><span className="stat-icon"><Icon size={21}/></span><div><strong>{n}</strong><span>{label}</span></div></div>)}</div>

    <section className="registry-panel practice-page">
      <div className="content-heading"><div><h3>Eligibility overview <span className="badge neutral">{filtered.length}</span></h3><p>{DEMO ? 'Showing patients assigned to this facility.' : 'Latest assessment and recertification status per patient.'}</p></div></div>
      <div className="registry-filters">
        <div className="filter-inline search-wide"><Search size={17}/><input aria-label="Search eligibility" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search patient name or MRN…"/></div>
        <select aria-label="Filter" value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="all">All patients</option>
          <option value="pending">Needs attention</option>
          <option value="overdue">Recert overdue</option>
        </select>
      </div>

      {loading ? <div className="generation-skeleton"><i/><i/><i/></div> : error ? <div className="inline-alert danger" role="alert">{error}</div> : !filtered.length ? <div className="empty-state"><Users size={28}/><h3>No patients found</h3><p>Try another name, MRN, or filter.</p></div> : (
        <div className="table-scroll">
          <table>
            <thead><tr><th>Patient</th><th>MRN</th><th>Admission</th><th>Eligibility</th><th>Program</th><th>Recertification</th><th>Action</th></tr></thead>
            <tbody>
              {filtered.map(p => {
                const st = p.eligibility_status ? STATUS[p.eligibility_status] : null;
                const enroll = p.enrollment;
                const recert = p.recertification || {};
                return <tr key={p.patient_id}>
                  <td><strong>{p.name}</strong></td>
                  <td>{p.mrn || '—'}</td>
                  <td>{p.admission_status || '—'}</td>
                  <td>{st ? <span className={`php-status-badge ${st.tone}`}>{st.label}</span> : <span className="php-status-badge pending">Not evaluated</span>}</td>
                  <td>{enroll ? `${enroll.program_type?.toUpperCase()} · ${enroll.status}` : '—'}</td>
                  <td>{recert.overdue ? <span className="badge-danger badge">Overdue {recert.days_until_due != null ? `(${Math.abs(recert.days_until_due)}d)` : ''}</span> : recert.upcoming ? <span className="badge-warn badge">Due soon</span> : recert.next_due_date ? <span className="badge-neutral badge">{recert.next_due_date}</span> : '—'}</td>
                  <td>{hasCap(user, 'chart.view') && <button className="text-button" onClick={() => openChart(p)}>Open chart <ArrowRight size={13}/></button>}</td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  </>;
}
